import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  fetchGdTopicById, createGdTopic, updateGdTopic,
  fetchGdCategories, addGdTopicLink, deleteGdTopicLink,
} from "./adminGdService";
import { fetchVocabularyList } from "../blog/vocabulary/adminVocabularyService";
import ImagePickerModal from "../media_images/ImagePickerModal";
import RichTextEditor from "../components/rich_text_editor/RichTextEditor";
import TopicLinksPanel from "../content/TopicLinksPanel";

const BASE_ROOT = process.env.REACT_APP_BASE_URL_ROOT || "";
const toPublicImgSrc = (url) => {
  if (!url) return "";
  const pub = url.replace(/\/api\/admin\/media-images\//, "/api/public/media-images/");
  console.log("[GdTopicForm] img raw:", url, "→ resolved:", `${BASE_ROOT}${pub}`);
  return `${BASE_ROOT}${pub}`;
};
const DIFFICULTIES = ["BEGINNER", "INTERMEDIATE", "ADVANCED"];
const TABS = ["Basic Info", "Thumbnail", "Content", "Key Points", "Sample Answers", "Dos & Don'ts", "Vocabulary", "Images", "Videos", "Links"];

const newKeyPoint = () => ({ point: "", sortOrder: 0 });
const newAnswer = () => ({ title: "", answer: "", sortOrder: 0 });
const newDoDont = (type) => ({ item: "", type, sortOrder: 0 });
const newVideo = () => ({ title: "", videoUrl: "", sortOrder: 0 });

// ── Inline bulk-paste panel ───────────────────────────────────────────────────
const BulkAddPanel = ({ label, placeholder, onAdd, accentColor = "#019095" }) => {
  const [open, setOpen] = useState(false);
  const [text, setText] = useState("");

  const handleAdd = () => {
    const lines = text.split("\n").map((l) => l.trim()).filter(Boolean);
    if (!lines.length) return;
    onAdd(lines);
    setText("");
    setOpen(false);
  };

  return (
    <div style={{ marginTop: 10 }}>
      <button
        onClick={() => setOpen((o) => !o)}
        style={{ padding: "6px 14px", borderRadius: 6, border: `1px dashed ${accentColor}`, color: accentColor, background: open ? accentColor + "12" : "none", cursor: "pointer", fontSize: 13, fontWeight: 600 }}
      >
        {open ? "▲ Close bulk add" : `⚡ Bulk add ${label}`}
      </button>

      {open && (
        <div style={{ marginTop: 10, background: "#f4fafa", border: `1px solid ${accentColor}44`, borderRadius: 8, padding: 14 }}>
          <p style={{ margin: "0 0 8px", fontSize: 13, color: "#555" }}>
            Type or paste — <strong>one item per line</strong>. Each line becomes a separate entry.
          </p>
          <textarea
            autoFocus
            value={text}
            onChange={(e) => setText(e.target.value)}
            placeholder={placeholder}
            style={{ width: "100%", minHeight: 140, padding: "10px 12px", borderRadius: 6, border: "1px solid #ccc", fontSize: 13, resize: "vertical", boxSizing: "border-box", fontFamily: "inherit", lineHeight: 1.6 }}
          />
          <div style={{ display: "flex", gap: 8, marginTop: 8 }}>
            <button
              onClick={handleAdd}
              disabled={!text.trim()}
              style={{ padding: "7px 20px", background: accentColor, color: "#fff", border: "none", borderRadius: 6, cursor: "pointer", fontWeight: 600, fontSize: 13, opacity: text.trim() ? 1 : 0.5 }}
            >
              Add {text.split("\n").filter((l) => l.trim()).length || 0} items
            </button>
            <button onClick={() => { setOpen(false); setText(""); }} style={{ padding: "7px 14px", border: "1px solid #ccc", background: "#fff", borderRadius: 6, cursor: "pointer", fontSize: 13 }}>
              Cancel
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

// ─────────────────────────────────────────────────────────────────────────────

const AdminGdTopicFormPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const isEdit = Boolean(id);

  const [tab, setTab] = useState(0);
  const [categories, setCategories] = useState([]);
  const [saving, setSaving] = useState(false);
  const [showImagePicker, setShowImagePicker] = useState(false);
  const [showThumbPicker, setShowThumbPicker] = useState(false);

  const [vocabSearch, setVocabSearch] = useState("");
  const [vocabOptions, setVocabOptions] = useState([]);

  const [links, setLinks] = useState([]);
  const [newLink, setNewLink] = useState({ refType: "EVENT", refId: "" });

  const [form, setForm] = useState({
    title: "", categoryId: "", difficulty: "BEGINNER", sortOrder: "",
    description: "",
    thumbnailImageId: null, thumbnailUrl: "",
    keyPoints: [newKeyPoint()],
    sampleAnswers: [newAnswer()],
    dosAndDonts: [newDoDont("DO"), newDoDont("DONT")],
    vocabularyIds: [],
    selectedVocabs: [],
    images: [],
    videos: [newVideo()],
  });

  useEffect(() => {
    fetchGdCategories().then((r) => {
      if (r.status === 200) setCategories(Array.isArray(r.data) ? r.data : r.data?.content || []);
    });
    if (isEdit) {
      fetchGdTopicById(id).then((r) => {
        if (r.status === 200 && r.data) {
          const d = r.data;
          setForm({
            title: d.title || "",
            categoryId: d.category?.id || "",
            difficulty: d.difficulty || "BEGINNER",
            sortOrder: d.sortOrder ?? "",
            description: d.description || "",
            thumbnailImageId: d.thumbnailImageId || null,
            thumbnailUrl: d.thumbnailUrl || "",
            keyPoints: d.keyPoints?.length ? d.keyPoints : [newKeyPoint()],
            sampleAnswers: d.sampleAnswers?.length ? d.sampleAnswers : [newAnswer()],
            dosAndDonts: d.dosAndDonts?.length ? d.dosAndDonts : [newDoDont("DO"), newDoDont("DONT")],
            vocabularyIds: d.vocabularies?.map((v) => v.id) || [],
            selectedVocabs: d.vocabularies || [],
            images: d.images?.map((img) => ({ mediaImageId: img.id, sortOrder: img.sortOrder, altText: img.altText || "", url: img.url })) || [],
            videos: d.videos?.length ? d.videos : [newVideo()],
          });
          setLinks(d.links || []);
        }
      });
    }
  }, [id, isEdit]);

  useEffect(() => {
    if (!vocabSearch.trim()) { setVocabOptions([]); return; }
    const t = setTimeout(async () => {
      const res = await fetchVocabularyList({ page: 0, size: 20, search: vocabSearch });
      if (res.status === 200) setVocabOptions(res.data?.content || []);
    }, 350);
    return () => clearTimeout(t);
  }, [vocabSearch]);

  const setField = (key, val) => setForm((f) => ({ ...f, [key]: val }));
  const updateList = (key, idx, field, val) =>
    setField(key, form[key].map((item, i) => i === idx ? { ...item, [field]: val } : item));
  const addToList = (key, template) => setField(key, [...form[key], template]);
  const removeFromList = (key, idx) => setField(key, form[key].filter((_, i) => i !== idx));

  const addVocab = (v) => {
    if (form.vocabularyIds.includes(v.id)) return;
    setField("vocabularyIds", [...form.vocabularyIds, v.id]);
    setField("selectedVocabs", [...form.selectedVocabs, v]);
    setVocabSearch("");
    setVocabOptions([]);
  };
  const removeVocab = (vid) => {
    setField("vocabularyIds", form.vocabularyIds.filter((x) => x !== vid));
    setField("selectedVocabs", form.selectedVocabs.filter((v) => v.id !== vid));
  };

  const handleImageSelect = (url, imageId) => {
    setForm((f) => ({
      ...f,
      images: [...f.images, { mediaImageId: imageId || null, sortOrder: f.images.length, altText: "", url }],
    }));
  };

  const handleSave = async () => {
    if (!form.title.trim()) return alert("Title is required");
    setSaving(true);

    const validImages = form.images.filter((img) => img.mediaImageId != null);

    const payload = {
      title: form.title.trim(),
      description: form.description,
      categoryId: form.categoryId ? Number(form.categoryId) : null,
      difficulty: form.difficulty,
      sortOrder: form.sortOrder !== "" ? Number(form.sortOrder) : null,
      thumbnailImageId: form.thumbnailImageId ? Number(form.thumbnailImageId) : null,
      keyPoints: form.keyPoints.filter((k) => k.point.trim()).map((k, i) => ({ point: k.point, sortOrder: i })),
      sampleAnswers: form.sampleAnswers.filter((a) => a.answer.trim()).map((a, i) => ({ title: a.title, answer: a.answer, sortOrder: i })),
      dosAndDonts: form.dosAndDonts.filter((d) => d.item.trim()).map((d, i) => ({ item: d.item, type: d.type, sortOrder: i })),
      vocabularyIds: form.vocabularyIds,
      images: validImages.map((img, i) => ({ mediaImageId: Number(img.mediaImageId), sortOrder: i, altText: img.altText })),
      videos: form.videos.filter((v) => v.videoUrl.trim()).map((v, i) => ({ title: v.title, videoUrl: v.videoUrl, sortOrder: i })),
    };

    const res = isEdit ? await updateGdTopic(id, payload) : await createGdTopic(payload);
    setSaving(false);

    if (res.status === 200 || res.status === 201) {
      navigate("/admin/gd/topics");
    } else if (res.message?.includes("MediaImage not found")) {
      const badId = res.message.match(/(\d+)/)?.[1];
      const cleaned = validImages.filter((img) => String(img.mediaImageId) !== String(badId));
      setField("images", cleaned);
      const retryPayload = { ...payload, images: cleaned.map((img, i) => ({ mediaImageId: Number(img.mediaImageId), sortOrder: i, altText: img.altText })) };
      setSaving(true);
      const retryRes = isEdit ? await updateGdTopic(id, retryPayload) : await createGdTopic(retryPayload);
      setSaving(false);
      if (retryRes.status === 200 || retryRes.status === 201) {
        alert(`Note: Image ID ${badId} was no longer valid and was removed automatically. Topic saved successfully.`);
        navigate("/admin/gd/topics");
      } else {
        alert(retryRes.message || "Save failed after retry");
      }
    } else {
      alert(res.message || "Save failed");
    }
  };

  const handleAddLink = async () => {
    if (!newLink.refId) return alert("refId is required");
    const res = await addGdTopicLink(id, { refType: newLink.refType, refId: Number(newLink.refId) });
    if (res.status === 200 || res.status === 201) { setLinks((l) => [...l, res.data]); setNewLink({ refType: "EVENT", refId: "" }); }
    else alert(res.message || "Failed to add link");
  };

  const handleRemoveLink = async (linkId) => {
    if (!window.confirm("Remove this link?")) return;
    const res = await deleteGdTopicLink(id, linkId);
    if (res.status === 200 || res.status === 204) setLinks((l) => l.filter((x) => x.id !== linkId));
    else alert(res.message || "Failed");
  };

  const inputStyle = { width: "100%", padding: "8px 12px", borderRadius: 6, border: "1px solid #ccc", boxSizing: "border-box" };
  const taStyle = { ...inputStyle, minHeight: 120, resize: "vertical", fontFamily: "monospace", fontSize: 13 };

  return (
    <div className="admin-container">
      <div className="admin-header">
        <h2>{isEdit ? "Edit GD Topic" : "New GD Topic"}</h2>
        <div style={{ display: "flex", gap: 8 }}>
          <button onClick={() => navigate("/admin/gd/topics")} style={{ padding: "8px 16px", borderRadius: 6, border: "1px solid #ccc", background: "#fff", cursor: "pointer" }}>Cancel</button>
          <button onClick={handleSave} disabled={saving} style={{ padding: "8px 20px", background: "#019095", color: "#fff", border: "none", borderRadius: 6, cursor: "pointer" }}>
            {saving ? "Saving..." : "Save"}
          </button>
        </div>
      </div>

      {/* Tabs */}
      <div style={{ display: "flex", gap: 4, flexWrap: "wrap", marginBottom: 20, borderBottom: "2px solid #eee", paddingBottom: 0 }}>
        {TABS.map((t, i) => (
          <button key={t} onClick={() => setTab(i)}
            style={{ padding: "8px 14px", border: "none", background: "none", cursor: "pointer", fontWeight: tab === i ? 700 : 400, color: tab === i ? "#019095" : "#555", borderBottom: tab === i ? "2px solid #019095" : "2px solid transparent", marginBottom: -2, fontSize: 13 }}>
            {t}
          </button>
        ))}
      </div>

      {/* Tab 0: Basic Info */}
      {tab === 0 && (
        <div style={{ display: "flex", flexDirection: "column", gap: 14, maxWidth: 600 }}>
          <div>
            <label style={{ fontSize: 13, fontWeight: 600 }}>Title *</label>
            <input style={inputStyle} value={form.title} onChange={(e) => setField("title", e.target.value)} placeholder="Topic title" />
          </div>
          <div>
            <label style={{ fontSize: 13, fontWeight: 600 }}>Category</label>
            <select style={inputStyle} value={form.categoryId} onChange={(e) => setField("categoryId", e.target.value)}>
              <option value="">— Select Category —</option>
              {categories.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
          </div>
          <div>
            <label style={{ fontSize: 13, fontWeight: 600 }}>Difficulty</label>
            <select style={inputStyle} value={form.difficulty} onChange={(e) => setField("difficulty", e.target.value)}>
              {DIFFICULTIES.map((d) => <option key={d}>{d}</option>)}
            </select>
          </div>
          <div>
            <label style={{ fontSize: 13, fontWeight: 600 }}>Sort Order</label>
            <input type="number" style={inputStyle} value={form.sortOrder} onChange={(e) => setField("sortOrder", e.target.value)} />
          </div>
        </div>
      )}

      {/* Tab 1: Thumbnail */}
      {tab === 1 && (
        <div style={{ maxWidth: 500 }}>
          <label style={{ fontSize: 13, fontWeight: 600, display: "block", marginBottom: 10 }}>Thumbnail Image</label>
          {form.thumbnailUrl ? (
            <div style={{ marginBottom: 16 }}>
              <img src={toPublicImgSrc(form.thumbnailUrl)} alt="thumbnail"
                style={{ width: "100%", maxHeight: 240, objectFit: "cover", borderRadius: 10, border: "1px solid #ddd" }} />
              <div style={{ display: "flex", gap: 8, marginTop: 10 }}>
                <button onClick={() => setShowThumbPicker(true)}
                  style={{ padding: "7px 16px", borderRadius: 6, border: "1px solid #019095", color: "#019095", background: "#fff", cursor: "pointer" }}>
                  Change Thumbnail
                </button>
                <button onClick={() => setField("thumbnailImageId", null) || setField("thumbnailUrl", "")}
                  style={{ padding: "7px 16px", borderRadius: 6, border: "1px solid #e53935", color: "#e53935", background: "#fff", cursor: "pointer" }}>
                  Remove
                </button>
              </div>
            </div>
          ) : (
            <div
              onClick={() => setShowThumbPicker(true)}
              style={{ width: "100%", height: 200, border: "2px dashed #ccc", borderRadius: 10, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", cursor: "pointer", background: "#fafafa", gap: 8 }}
              onMouseEnter={(e) => e.currentTarget.style.borderColor = "#019095"}
              onMouseLeave={(e) => e.currentTarget.style.borderColor = "#ccc"}
            >
              <span style={{ fontSize: 36 }}>🖼️</span>
              <span style={{ fontSize: 14, color: "#888" }}>Click to pick a thumbnail image</span>
            </div>
          )}
          <ImagePickerModal
            isOpen={showThumbPicker}
            onClose={() => setShowThumbPicker(false)}
            onSelect={(url, imageId) => { setField("thumbnailImageId", imageId); setField("thumbnailUrl", url); setShowThumbPicker(false); }}
            defaultImageUse="GENERAL"
          />
        </div>
      )}

      {/* Tab 2: Content */}
      {tab === 2 && (
        <div>
          <label style={{ fontSize: 13, fontWeight: 600, display: "block", marginBottom: 8 }}>Description</label>
          <RichTextEditor
            value={form.description}
            onChange={(val) => setField("description", val)}
            placeholder="Type or paste content here…"
          />
        </div>
      )}

      {/* Tab 3: Key Points */}
      {tab === 3 && (
        <div>
          {form.keyPoints.map((kp, i) => (
            <div key={i} style={{ display: "flex", gap: 8, marginBottom: 8, alignItems: "center" }}>
              <span style={{ minWidth: 24, color: "#888", fontSize: 13 }}>{i + 1}.</span>
              <input
                style={{ ...inputStyle, flex: 1 }}
                value={kp.point}
                onChange={(e) => updateList("keyPoints", i, "point", e.target.value)}
                placeholder="Key point..."
              />
              <button onClick={() => removeFromList("keyPoints", i)} style={{ color: "#e53935", background: "none", border: "none", cursor: "pointer", fontSize: 18 }}>×</button>
            </div>
          ))}
          <button onClick={() => addToList("keyPoints", newKeyPoint())} style={{ marginTop: 4, padding: "6px 14px", borderRadius: 6, border: "1px dashed #019095", color: "#019095", background: "none", cursor: "pointer" }}>+ Add One</button>
          <BulkAddPanel
            label="key points"
            placeholder={"Be confident while speaking\nMaintain eye contact\nListen actively to others\nUse structured arguments"}
            onAdd={(lines) => setField("keyPoints", [...form.keyPoints, ...lines.map((l) => ({ point: l, sortOrder: 0 }))])}
          />
        </div>
      )}

      {/* Tab 4: Sample Answers */}
      {tab === 4 && (
        <div>
          {form.sampleAnswers.map((ans, i) => (
            <div key={i} style={{ background: "#f9f9f9", borderRadius: 8, padding: 12, marginBottom: 12 }}>
              <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 8 }}>
                <span style={{ fontWeight: 600, fontSize: 13 }}>Answer {i + 1}</span>
                <button onClick={() => removeFromList("sampleAnswers", i)} style={{ color: "#e53935", background: "none", border: "none", cursor: "pointer" }}>Remove</button>
              </div>
              <input style={{ ...inputStyle, marginBottom: 8 }} value={ans.title} onChange={(e) => updateList("sampleAnswers", i, "title", e.target.value)} placeholder="Optional label (e.g. Sample 1)" />
              <RichTextEditor
                value={ans.answer}
                onChange={(val) => updateList("sampleAnswers", i, "answer", val)}
                placeholder="Type or paste answer here…"
              />
            </div>
          ))}
          <button onClick={() => addToList("sampleAnswers", newAnswer())} style={{ padding: "6px 14px", borderRadius: 6, border: "1px dashed #019095", color: "#019095", background: "none", cursor: "pointer" }}>+ Add One</button>
          <BulkAddPanel
            label="sample answers"
            placeholder={"In my opinion, technology has more benefits than drawbacks...\nI strongly believe that education should be free for all...\nFrom my perspective, climate change is the biggest challenge..."}
            onAdd={(lines) => setField("sampleAnswers", [...form.sampleAnswers, ...lines.map((l) => ({ title: "", answer: l, sortOrder: 0 }))])}
          />
        </div>
      )}

      {/* Tab 5: Dos & Don'ts */}
      {tab === 5 && (
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 20 }}>
          {["DO", "DONT"].map((type) => (
            <div key={type}>
              <h4 style={{ color: type === "DO" ? "#4caf50" : "#e53935", marginBottom: 10 }}>{type === "DO" ? "✅ Do's" : "❌ Don'ts"}</h4>
              {form.dosAndDonts.filter((d) => d.type === type).map((d) => {
                const idx = form.dosAndDonts.indexOf(d);
                return (
                  <div key={idx} style={{ display: "flex", gap: 6, marginBottom: 8 }}>
                    <input
                      style={{ ...inputStyle, flex: 1 }}
                      value={d.item}
                      onChange={(e) => updateList("dosAndDonts", idx, "item", e.target.value)}
                      placeholder={`${type === "DO" ? "Do" : "Don't"}...`}
                    />
                    <button onClick={() => removeFromList("dosAndDonts", idx)} style={{ color: "#e53935", background: "none", border: "none", cursor: "pointer", fontSize: 18 }}>×</button>
                  </div>
                );
              })}
              <button
                onClick={() => addToList("dosAndDonts", newDoDont(type))}
                style={{ padding: "5px 12px", borderRadius: 6, border: `1px dashed ${type === "DO" ? "#4caf50" : "#e53935"}`, color: type === "DO" ? "#4caf50" : "#e53935", background: "none", cursor: "pointer", fontSize: 12 }}
              >+ Add One</button>
              <BulkAddPanel
                label={type === "DO" ? "do's" : "don'ts"}
                placeholder={type === "DO"
                  ? "Speak clearly and confidently\nUse examples to support your point\nListen to others before responding"
                  : "Don't interrupt others\nAvoid using filler words\nDon't go off-topic"}
                accentColor={type === "DO" ? "#4caf50" : "#e53935"}
                onAdd={(lines) => setField("dosAndDonts", [...form.dosAndDonts, ...lines.map((l) => ({ item: l, type, sortOrder: 0 }))])}
              />
            </div>
          ))}
        </div>
      )}

      {/* Tab 6: Vocabulary */}
      {tab === 6 && (
        <div>
          <div style={{ position: "relative", maxWidth: 400, marginBottom: 16 }}>
            <input style={inputStyle} value={vocabSearch} onChange={(e) => setVocabSearch(e.target.value)} placeholder="Search vocabulary to add..." />
            {vocabOptions.length > 0 && (
              <div style={{ position: "absolute", top: "100%", left: 0, right: 0, background: "#fff", border: "1px solid #ddd", borderRadius: 6, zIndex: 10, maxHeight: 200, overflowY: "auto" }}>
                {vocabOptions.map((v) => (
                  <div key={v.id} onClick={() => addVocab(v)}
                    style={{ padding: "8px 12px", cursor: "pointer", borderBottom: "1px solid #f0f0f0", fontSize: 13 }}
                    onMouseEnter={(e) => e.currentTarget.style.background = "#f5f5f5"}
                    onMouseLeave={(e) => e.currentTarget.style.background = "#fff"}>
                    <strong>{v.word}</strong> — {v.definition}
                  </div>
                ))}
              </div>
            )}
          </div>
          <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
            {form.selectedVocabs.map((v) => (
              <span key={v.id} style={{ background: "#e3f2fd", color: "#1976d2", borderRadius: 999, padding: "4px 12px", fontSize: 13, display: "flex", alignItems: "center", gap: 6 }}>
                {v.word}
                <button onClick={() => removeVocab(v.id)} style={{ background: "none", border: "none", cursor: "pointer", color: "#1976d2", fontSize: 14, lineHeight: 1 }}>×</button>
              </span>
            ))}
            {form.selectedVocabs.length === 0 && <p style={{ color: "#888", fontSize: 13 }}>No vocabulary added yet.</p>}
          </div>
        </div>
      )}

      {/* Tab 7: Images */}
      {tab === 7 && (
        <div>
          <button onClick={() => setShowImagePicker(true)} style={{ marginBottom: 16, padding: "8px 16px", borderRadius: 6, border: "1px solid #019095", color: "#019095", background: "#fff", cursor: "pointer" }}>+ Add Image</button>
          <div style={{ display: "flex", flexWrap: "wrap", gap: 12 }}>
            {form.images.map((img, i) => (
              <div key={i} style={{ border: "1px solid #ddd", borderRadius: 8, padding: 8, width: 160 }}>
                <img src={toPublicImgSrc(img.url)} alt={img.altText} style={{ width: "100%", height: 100, objectFit: "cover", borderRadius: 4, marginBottom: 6 }} />
                <input style={{ ...inputStyle, fontSize: 12, marginBottom: 4 }} value={img.altText} onChange={(e) => {
                  const imgs = [...form.images]; imgs[i] = { ...imgs[i], altText: e.target.value }; setField("images", imgs);
                }} placeholder="Alt text" />
                <button onClick={() => removeFromList("images", i)} style={{ width: "100%", padding: "4px", background: "#ffebee", color: "#e53935", border: "none", borderRadius: 4, cursor: "pointer", fontSize: 12 }}>Remove</button>
              </div>
            ))}
          </div>
          <ImagePickerModal isOpen={showImagePicker} onClose={() => setShowImagePicker(false)} onSelect={handleImageSelect} defaultImageUse="GENERAL" />
        </div>
      )}

      {/* Tab 8: Videos */}
      {tab === 8 && (
        <div>
          {form.videos.map((v, i) => (
            <div key={i} style={{ background: "#f9f9f9", borderRadius: 8, padding: 12, marginBottom: 10 }}>
              <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 8 }}>
                <span style={{ fontWeight: 600, fontSize: 13 }}>Video {i + 1}</span>
                <button onClick={() => removeFromList("videos", i)} style={{ color: "#e53935", background: "none", border: "none", cursor: "pointer" }}>Remove</button>
              </div>
              <input style={{ ...inputStyle, marginBottom: 8 }} value={v.title} onChange={(e) => updateList("videos", i, "title", e.target.value)} placeholder="Video title (e.g. Session Recording - Batch 3)" />
              <input style={inputStyle} value={v.videoUrl} onChange={(e) => updateList("videos", i, "videoUrl", e.target.value)} placeholder="https://youtube.com/..." />
            </div>
          ))}
          <button onClick={() => addToList("videos", newVideo())} style={{ padding: "6px 14px", borderRadius: 6, border: "1px dashed #019095", color: "#019095", background: "none", cursor: "pointer" }}>+ Add Video</button>
        </div>
      )}

      {/* Tab 9: Links */}
      {tab === 9 && (
        <div>
          {!isEdit && <p style={{ color: "#888" }}>Save the topic first to manage linked sessions.</p>}
          {isEdit && (
            <>
              <div style={{ display: "flex", gap: 10, marginBottom: 16, flexWrap: "wrap" }}>
                <select value={newLink.refType} onChange={(e) => setNewLink((l) => ({ ...l, refType: e.target.value }))}
                  style={{ padding: "8px 12px", borderRadius: 6, border: "1px solid #ccc" }}>
                  <option value="EVENT">EVENT</option>
                  <option value="COURSE_LESSON">COURSE_LESSON</option>
                </select>
                <input type="number" placeholder="Ref ID" value={newLink.refId} onChange={(e) => setNewLink((l) => ({ ...l, refId: e.target.value }))}
                  style={{ width: 120, padding: "8px 12px", borderRadius: 6, border: "1px solid #ccc" }} />
                <button onClick={handleAddLink} style={{ padding: "8px 16px", background: "#019095", color: "#fff", border: "none", borderRadius: 6, cursor: "pointer" }}>Add Link</button>
              </div>
              {links.length === 0 ? <p style={{ color: "#888" }}>No linked sessions.</p> : (
                <table className="announcement-table">
                  <thead><tr><th>Type</th><th>Ref ID</th><th>Action</th></tr></thead>
                  <tbody>
                    {links.map((lk) => (
                      <tr key={lk.id}>
                        <td>{lk.refType}</td>
                        <td>{lk.refId}</td>
                        <td><button className="delete-btn" onClick={() => handleRemoveLink(lk.id)}>Remove</button></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
              <TopicLinksPanel topicType="GD_TOPIC" topicId={Number(id)} />
            </>
          )}
        </div>
      )}
    </div>
  );
};

export default AdminGdTopicFormPage;
